import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function EnquiryModal({ open, course, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", email: "", phone: "", message: "" });
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, course: course?.title || "General enquiry" });
      toast.success("Enquiry sent! We will get back to you soon.");
      onClose();
    } catch (err) {
      toast.error("Could not send your enquiry. Please try WhatsApp or email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="enquiry-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="bg-navy px-7 py-6 flex items-start justify-between">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-sky">Notify Me / Enquire</p>
                <h3 className="mt-2 font-serif text-2xl font-semibold text-white leading-snug" data-testid="enquiry-course-name">
                  {course?.title || "General Enquiry"}
                </h3>
              </div>
              <button onClick={onClose} data-testid="enquiry-modal-close" className="text-white/60 hover:text-white transition-colors p-1" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submit} className="p-7 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  data-testid="enquiry-name-input"
                  className="w-full rounded-xl border border-navy/15 bg-chalk/50 px-4 py-3 text-sm font-sans text-navy placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-cherry/40"
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email address"
                  data-testid="enquiry-email-input"
                  className="w-full rounded-xl border border-navy/15 bg-chalk/50 px-4 py-3 text-sm font-sans text-navy placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-cherry/40"
                />
              </div>
              <input
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone / WhatsApp (optional)"
                data-testid="enquiry-phone-input"
                className="w-full rounded-xl border border-navy/15 bg-chalk/50 px-4 py-3 text-sm font-sans text-navy placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-cherry/40"
              />
              <textarea
                rows={3}
                value={form.message}
                onChange={set("message")}
                placeholder="Anything you'd like to ask? (optional)"
                data-testid="enquiry-message-input"
                className="w-full rounded-xl border border-navy/15 bg-chalk/50 px-4 py-3 text-sm font-sans text-navy placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-cherry/40 resize-none"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-modal-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-cherry hover:bg-[#B81919] disabled:opacity-60 text-white font-medium px-6 py-3.5 rounded-full transition-colors duration-300"
              >
                <Send className="h-4 w-4" />
                {sending ? "Sending..." : "Send Enquiry"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
